// Basic
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DateTime } from 'luxon';
// Store & actions
import useEnvelopesStore from '@/app/store/modules/envelopes';
import actionsEnvelopes from '../../../api/actionsEnvelopes';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { DateInput } from '@/common-utils/components/ui/dateInput';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import envelopesFields from '../../../fields/envelopesFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import downloadCodesFields from '../../../fields/downloadCodesFields';
// Lang
import { nsEnvelope } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.downloadCode
 * @returns {JSX} ModalCreateOrUpdateDownloadCode
 */
function ModalCreateOrUpdateDownloadCode({ isOpen, setIsOpen, downloadCode = null }) {
    //Constants
    const {
        uuid: downloadCodeId,
        downloadCodeName: codeNameFromProps,
        scheduledStartDate: scheduledStartDateFromProps,
        scheduledEndDate: scheduledEndDateFromProps,
        accessCode: accessCodeFromProps,
        downloadServerIpAddressPort: ipServerFromProps
    } = downloadCode ?? {};
    const todayDate = new Date();

    // Toast
    const { toast } = useToast();

    // Store
    const storeRefetchDownloadCodes = useEnvelopesStore(s => s.storeRefetchDownloadCodes);

    // State
    const [disabledValidation, setDisabledValidation] = useState(true);

    // Function
    const setDateType = (dateInput) => {
        return typeof dateInput === 'string' || dateInput instanceof Date ? new Date(dateInput) : undefined;
    };

    // Form validation
    const accessCodeSchema = z
        .string()
        .refine((accessCode) => /^\d{4,9}$/.test(accessCode), { message: '' });
    const ipServerSchema = z
        .string()
        .refine((ip) =>
            // eslint-disable-next-line max-len
            /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d):([1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5])$/
                .test(ip), {
            message: ''
        });
    const formSchema = z.object({
        codeName: z.string().nonempty(),
        scheduledStartDate: z.preprocess(setDateType, z.date().optional()),
        scheduledEndDate: z.preprocess(setDateType, z.date().optional()),
        accessCode: accessCodeSchema,
        ipServer: ipServerSchema
    })
        .refine((values) => !values.scheduledEndDate || values.scheduledEndDate > todayDate, { path: ['scheduledEndDate'] })
        .refine((values) => !values.scheduledStartDate || values.scheduledStartDate > todayDate, { path: ['scheduledStartDate'] })
        .superRefine((values, ctx) => {
            const { scheduledStartDate, scheduledEndDate } = values;

            // Start and end dates are both empty =>  valid
            if (!scheduledStartDate && !scheduledEndDate) {
                return;
            }

            // If only end date is set => valid
            if (!scheduledStartDate && scheduledEndDate) {
                return;
            }

            // If start date is set without end date => Error
            if (scheduledStartDate && !scheduledEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['scheduledEndDate']
                });
                return;
            }

            // If both dates exist but with a wrong order => Error
            if (scheduledStartDate >= scheduledEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['scheduledStartDate']
                });
            }
        });

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            codeName: codeNameFromProps ?? '',
            scheduledStartDate: scheduledStartDateFromProps ?? undefined,
            scheduledEndDate: scheduledEndDateFromProps ?? undefined,
            accessCode: accessCodeFromProps ?? '',
            ipServer: ipServerFromProps ?? ''
        }
    });

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchDownloadCodes(true);
        toast({
            description: <Entity
                namespace={nsEnvelope}
                entity={downloadCode ? 'modal.update_download_code.success' : 'modal.create_download_code.success'}
            />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const erroCode = error.response?.data?.errorCode ?? error.code;
        const errorEntity = erroCode === 'ALREADY_EXISTING_ACCESS_OR_CODE_NAME' ? erroCode : 'UNKNOWN_ERROR';
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsEnvelope} entity={`modal.errors.${errorEntity}`} />,
            variant: 'danger'
        });
    };
    const createDownloadCode = actionsEnvelopes.createDownloadCode(onSuccess, onError);
    const updateDownloadCode = actionsEnvelopes.updateDownloadCode(onSuccess, onError);

    // Hook
    useEffect(() => {
        // Trigger validation
        form.trigger(['accessCode', 'ipServer', 'scheduledStartDate', 'scheduledEndDate']);

        setDisabledValidation(!form.formState.isValid);
    }, [
        form.watch('scheduledStartDate'),
        form.watch('scheduledEndDate'),
        form.watch('accessCode'),
        form.watch('ipServer'),
        form.formState.isValid]
    );

    return <Modal open={isOpen} onOpenChange={setIsOpen}>
        <ModalContent className='max-w-3xl'>
            <HeaderTitle
                title={'modal.create_download_code.title'}
                namespace={nsEnvelope}
            />
            <ModalBody className='modal-max-height overflow-auto'>
                <Form {...form}>
                    <form className='update-password-form space-y-2'>
                        <FormField
                            control={form.control}
                            name='codeName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>
                                        <Entity namespace={nsEnvelope} entity='keys.envelopes.code_name' />
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            autoComplete='off'
                                            entity='form.placeholder.code_name'
                                            namespace={nsEnvelope}
                                            {...field} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='scheduledStartDate'
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        <Entity namespace={nsEnvelope} entity='keys.envelopes.schedule_start' />
                                    </FormLabel>
                                    <FormControl>
                                    </FormControl>
                                    <DateInput
                                        selected={field.value}
                                        onSelect={(date) => field.onChange(date)}
                                        placeholder={{ namespace: nsEnvelope, entity: 'form.placeholder.start_date' }}
                                        disableBeforeToday={true}
                                        className={fieldState.error ? 'border border-danger' : ''}
                                    />
                                </FormItem>
                            )
                            }
                        />
                        <FormField
                            control={form.control}
                            name='scheduledEndDate'
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        <Entity namespace={nsEnvelope} entity='keys.envelopes.schedule_end' />
                                    </FormLabel>
                                    <FormControl>
                                        <DateInput
                                            selected={field.value}
                                            onSelect={(date) => field.onChange(date)}
                                            placeholder={{ namespace: nsEnvelope, entity: 'form.placeholder.end_date' }}
                                            disableBeforeToday={true}
                                            className={fieldState.error ? 'border border-danger' : ''}
                                        />
                                    </FormControl>
                                </FormItem>
                            )
                            }
                        />
                        <FormField
                            control={form.control}
                            name='accessCode'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>
                                        <Entity namespace={nsEnvelope} entity='keys.envelopes.access_code' />
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            autoComplete='off'
                                            entity='form.placeholder.access_code'
                                            namespace={nsEnvelope}
                                            {...field} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='ipServer'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>
                                        <Entity namespace={nsEnvelope} entity='keys.envelopes.server_ip' />
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            autoComplete='off'
                                            entity='form.placeholder.server_ip'
                                            namespace={nsEnvelope}
                                            {...field} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={() => {
                    const { codeName, scheduledStartDate, scheduledEndDate, accessCode, ipServer } = form.getValues();

                    const downloadCodeDataToSend = {
                        [downloadCodesFields.DOWNLOAD_CODE_NAME.key]: codeName,
                        [envelopesFields.SCHEDULED_START_DATE.key]: downloadCode ?
                            scheduledStartDate : DateTime.fromJSDate(scheduledStartDate).toISO(),
                        [envelopesFields.SCHEDULED_END_DATE.key]: downloadCode ?
                            scheduledEndDate : DateTime.fromJSDate(scheduledEndDate).toISO(),
                        [envelopesFields.ACCESS_CODE.key]: accessCode,
                        [envelopesFields.DOWNLOAD_SERVER_IP_ADDRESS_PORT.key]: ipServer
                    };

                    if (downloadCode) {
                        updateDownloadCode.mutate({ downloadCodeUuid: downloadCodeId, downloadCodeDataToSend });
                    }
                    else {
                        createDownloadCode.mutate({ downloadCodeDataToSend });
                    }
                }
                }
                isSubmitDisabled={disabledValidation}
                isSubmitting={createDownloadCode.isLoading || updateDownloadCode.isLoading}
            />
        </ModalContent>
    </Modal>;
}

ModalCreateOrUpdateDownloadCode.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    downloadCode: PropTypes.shape({
        uuid: PropTypes.string,
        downloadCodeName: PropTypes.string,
        scheduledStartDate: PropTypes.string,
        scheduledEndDate: PropTypes.string,
        accessCode: PropTypes.string,
        downloadServerIpAddressPort: PropTypes.string
    })
};

export default ModalCreateOrUpdateDownloadCode;